import { isDefaultVintageYear } from '@helpers/vintage';
import {
  Anchor,
  AnchorStyle,
  SORT_BY_OPTIONS,
  isMixedVintageWine,
  isVintageWine,
  sortVintages,
  useI18N,
} from '@vivino/js-web-common';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { fetchCheckoutPrices } from 'vivino-js/api/prices';
import { vintage, wine } from 'vivino-js/apiPropTypes';
import {
  CART_ITEM_SOURCE_TYPES,
  CartItemSourceContext,
} from 'vivino-js/context/CartItemSourceContext';

import { BaseModal } from '@components/BaseModal/BaseModal';

import VintageListPresentational, { MAX_VINTAGES_ON_WINEPAGE } from './VintageList.presentational';
import styles from './vintageList.scss';

const TRANSLATIONS = {
  showAll: 'common.show_all',
  recommendedSort: 'common.recommended_sort',
  yearSort: 'common.year_sort',
  popularSort: 'common.popular_sort',
  highestUserRatingSort: 'common.highest_user_rating_sort',
  lowestPriceFirstSort: 'common.lowest_price_sort',
  highestPriceFirstSort: 'common.highest_price_sort',
};

const getSortmenuText = (t) => [
  { id: SORT_BY_OPTIONS.recommended, name: t(TRANSLATIONS.recommendedSort) },
  { id: SORT_BY_OPTIONS.year, name: t(TRANSLATIONS.yearSort) },
  { id: SORT_BY_OPTIONS.popular, name: t(TRANSLATIONS.popularSort) },
  {
    id: SORT_BY_OPTIONS.highestUserRating,
    name: t(TRANSLATIONS.highestUserRatingSort),
  },
  {
    id: SORT_BY_OPTIONS.lowestPriceFirst,
    name: t(TRANSLATIONS.lowestPriceFirstSort),
  },
  {
    id: SORT_BY_OPTIONS.highestPriceFirst,
    name: t(TRANSLATIONS.highestPriceFirstSort),
  },
];

const VintageList = ({ vintages, wine, highlights }) => {
  const { t } = useI18N();
  let filteredVintages = vintages;
  if (isVintageWine(wine) || isMixedVintageWine(wine)) {
    // elements in `vintages` does not contain `wine` object so cannot use `isUnknownVintage`
    // thus use `isDefaultVintageYear` instead
    filteredVintages = vintages.filter((vintage) => !isDefaultVintageYear(vintage.year));
  }

  const [isLoading, setIsLoading] = useState(true);
  const [checkoutPrices, setCheckoutPrices] = useState({});
  const [sortedVintages, setSortedVintages] = useState([]);
  const [sortByKey, setSortByKey] = useState(SORT_BY_OPTIONS.recommended);
  const [showAll, setShowAll] = useState(false);

  const onSortByChanged = ({ target }) => {
    const { id } = getSortmenuText(t).find(
      (item) => item.id.toString() === target.value.toString()
    );
    setSortByKey(id);
    setSortedVintages(
      sortVintages({
        vintages: filteredVintages,
        checkoutPrices,
        highlights,
        sortByOption: id,
      })
    );
  };

  useEffect(() => {
    if (filteredVintages && filteredVintages.length) {
      const fetchPriceData = async () => {
        const checkoutPricesData = await fetchCheckoutPrices(wine.id);

        const vintageIdsToCheckoutPricesHash = checkoutPricesData?.checkout_prices?.reduce(
          (acc, vintage) => ({
            ...acc,
            ...{ [vintage.availability?.vintage?.id]: vintage },
          }),
          {}
        );

        setCheckoutPrices(vintageIdsToCheckoutPricesHash);
        setSortedVintages(
          sortVintages({
            vintages: filteredVintages,
            checkoutPrices,
            highlights,
          })
        );
        setIsLoading(false);
      };

      fetchPriceData();
    }
  }, []);

  if (!filteredVintages || !filteredVintages.length || wine.vintage_type === 1) {
    return null;
  }

  return (
    /* Ideally, CartItemSourceContext should be set in the parent component.
     * The caveat is that we will then have to repeat it in three places (master, vintage and offers).
     * Since VintageList is only coupled with wine pages, it is ok to set it here for now.
     */
    <CartItemSourceContext.Provider value={CART_ITEM_SOURCE_TYPES.WINE_PAGE_VINTAGE_COMPARISON}>
      <VintageListPresentational
        vintages={sortedVintages.slice(0, MAX_VINTAGES_ON_WINEPAGE)}
        wine={wine}
        highlights={highlights}
        pricesAndAvailabilities={checkoutPrices}
        isLoading={isLoading}
        sortByOptions={getSortmenuText(t)}
        onSortByChanged={onSortByChanged}
        sortByKey={sortByKey}
      />
      <BaseModal show={showAll} onClose={() => setShowAll(false)}>
        <VintageListPresentational
          vintages={sortedVintages}
          wine={wine}
          highlights={highlights}
          pricesAndAvailabilities={checkoutPrices}
          isLoading={isLoading}
          sortByOptions={getSortmenuText(t)}
          onSortByChanged={onSortByChanged}
          sortByKey={sortByKey}
          isInModal
        />
      </BaseModal>

      {vintages.length > MAX_VINTAGES_ON_WINEPAGE && (
        <Anchor
          onClick={() => setShowAll(true)}
          anchorStyle={AnchorStyle.VivinoLink}
          className={styles.showAll}
        >
          {t(TRANSLATIONS.showAll)}
        </Anchor>
      )}
    </CartItemSourceContext.Provider>
  );
};

VintageList.propTypes = {
  vintages: PropTypes.arrayOf(vintage).isRequired,
  wine: wine.isRequired,
  highlights: PropTypes.array,
};

export default VintageList;
